import { useEffect, useMemo } from 'react';
import { createFileRoute, Link, useRouterState } from '@tanstack/react-router';
import { z } from 'zod';

import { useAuth } from '@features/auth';
import {
  CrmCourseProfile,
  crmCourseProfileCertificationStateSchema,
  crmCourseProfilePeopleViewSchema,
  crmCourseProfileSortSchema,
  crmCourseProfileTabSchema,
  normalizeCrmCourseProfileReturnTo,
  resetCourseProfilePage,
  selectCourseProfilePage,
  selectCourseProfileNumberedPage,
  canRetreatCourseProfilePage,
  type CrmCourseProfilePeopleView,
  type CrmCourseProfileSort,
  type CrmCourseProfileTab,
} from '@features/crm-course-profile';

const courseProfileSearchSchema = z.object({
  courseProfileCertificationState: crmCourseProfileCertificationStateSchema.optional(),
  courseProfileCompletionFormCertificationType: z.uuid().optional(),
  courseProfileCertifiedFrom: z.iso.date().optional(),
  courseProfileCertifiedTo: z.iso.date().optional(),
  courseProfileCostMax: z.coerce.number().nonnegative().optional(),
  courseProfileCostMin: z.coerce.number().nonnegative().optional(),
  courseProfileCursor: z.string().trim().min(1).max(4096).optional(),
  courseProfileCursorHistory: z.array(z.string().trim().min(1).max(4096)).max(100).optional(),
  courseProfileInstructorPaymentStatus: z.string().trim().min(1).max(120).optional(),
  courseProfilePage: z.coerce.number().int().positive().optional(),
  courseProfilePeopleView: crmCourseProfilePeopleViewSchema.optional().catch(undefined),
  courseProfileQ: z.string().trim().max(120).optional(),
  courseProfileRegistrationReferenceType: z.string().trim().min(1).max(120).optional(),
  courseProfileRegistrationType: z.string().trim().min(1).max(120).optional(),
  courseProfileSort: crmCourseProfileSortSchema.optional(),
  courseProfileStudentPaymentStatus: z.string().trim().min(1).max(120).optional(),
  courseProfileSubmittedFrom: z.iso.date().optional(),
  courseProfileSubmittedTo: z.iso.date().optional(),
  courseProfileTab: crmCourseProfileTabSchema.optional().catch(undefined),
  returnTo: z
    .string()
    .max(4096)
    .optional()
    .transform((value) => normalizeCrmCourseProfileReturnTo(value)),
});

export const Route = createFileRoute('/$locale/_authenticated/crm/courses/$courseId')({
  component: CrmCourseProfileRoute,
  validateSearch: courseProfileSearchSchema,
});

function CrmCourseProfileRoute() {
  const navigate = Route.useNavigate();
  const { courseId, locale } = Route.useParams();
  const search = Route.useSearch();
  const { permissions, user } = useAuth();
  const viewerScope = useMemo(
    () =>
      user
        ? JSON.stringify({
            permissions: permissions ?? [],
            userId: user.id,
          })
        : '',
    [permissions, user]
  );
  const { currentLocation, requestedTab } = useRouterState({
    select: (state) => ({
      currentLocation: state.location.href,
      requestedTab: (state.location.search as { courseProfileTab?: unknown }).courseProfileTab,
    }),
  });

  const hasInvalidRequestedTab =
    requestedTab !== undefined && !crmCourseProfileTabSchema.safeParse(requestedTab).success;

  useEffect(() => {
    if (!hasInvalidRequestedTab) return;
    void navigate({
      replace: true,
      search: (current) => ({ ...current, courseProfileTab: undefined }),
    });
  }, [hasInvalidRequestedTab, navigate]);

  return (
    <main className="min-w-0 p-4 sm:p-6">
      <CrmCourseProfile
        certificationState={search.courseProfileCertificationState ?? 'To certify'}
        courseRef={courseId}
        hasPreviousPage={canRetreatCourseProfilePage(search)}
        onOpenPerson={(customerId) => {
          void navigate({
            to: '/$locale/crm/user-profiles/$userProfileId',
            params: { locale, userProfileId: customerId },
            search: { returnTo: currentLocation },
          });
        }}
        onOpenRegistrationProfile={(registrationId, customerId) => {
          void navigate({
            to: '/$locale/crm/user-profiles/$userProfileId/learning/registrations/$registrationId',
            params: { userProfileId: customerId, locale, registrationId },
            search: { returnTo: currentLocation },
          });
        }}
        cursor={search.courseProfileCursor}
        locale={locale}
        onBack={() => {
          if (search.returnTo !== undefined) {
            void navigate({ to: search.returnTo as never });
            return;
          }
          void navigate({ to: '/$locale/crm', params: { locale } });
        }}
        backDestination={search.returnTo === undefined ? 'crm' : 'customer'}
        onCertificationStateChange={(courseProfileCertificationState) => {
          void navigate({
            replace: true,
            search: (current) => ({
              ...resetCourseProfilePage(current),
              courseProfileCertificationState:
                courseProfileCertificationState === 'To certify'
                  ? undefined
                  : courseProfileCertificationState,
            }),
          });
        }}
        onCompletionFormCertificationTypeChange={(courseProfileCompletionFormCertificationType) => {
          void navigate({
            replace: true,
            search: (current) => ({
              ...resetCourseProfilePage(current),
              courseProfileCompletionFormCertificationType,
            }),
          });
        }}
        onPageChange={(targetPage, nextCursor) => {
          void navigate({
            search: (current) =>
              current.courseProfileTab === 'registrations'
                ? selectCourseProfileNumberedPage(current, targetPage)
                : selectCourseProfilePage(current, targetPage, nextCursor),
          });
        }}
        onPeopleViewChange={(courseProfilePeopleView: CrmCourseProfilePeopleView) => {
          void navigate({
            search: (current) => ({
              ...resetCourseProfilePage(current),
              courseProfilePeopleView,
            }),
          });
        }}
        onRegistrationFiltersChange={(filters) => {
          void navigate({
            replace: true,
            search: (current) => ({
              ...resetCourseProfilePage(current),
              courseProfileCertifiedFrom: filters.certifiedFrom,
              courseProfileCertifiedTo: filters.certifiedTo,
              courseProfileCostMax: filters.costMax,
              courseProfileCostMin: filters.costMin,
              courseProfileInstructorPaymentStatus: filters.instructorPaymentStatus,
              courseProfileRegistrationReferenceType: filters.registrationReferenceType,
              courseProfileRegistrationType: filters.registrationType,
              courseProfileStudentPaymentStatus: filters.studentPaymentStatus,
              courseProfileSubmittedFrom: filters.submittedFrom,
              courseProfileSubmittedTo: filters.submittedTo,
            }),
          });
        }}
        onSearchChange={(courseProfileQ) => {
          void navigate({
            replace: true,
            search: (current) => ({ ...resetCourseProfilePage(current), courseProfileQ }),
          });
        }}
        onSortChange={(courseProfileSort?: CrmCourseProfileSort) => {
          void navigate({
            replace: true,
            search: (current) => ({ ...resetCourseProfilePage(current), courseProfileSort }),
          });
        }}
        onTabChange={(courseProfileTab: CrmCourseProfileTab) => {
          void navigate({
            search: (current) => ({
              ...resetCourseProfilePage(current),
              courseProfileCertificationState: undefined,
              courseProfileCompletionFormCertificationType: undefined,
              courseProfilePeopleView: undefined,
              courseProfileQ: undefined,
              courseProfileSort: undefined,
              courseProfileTab: courseProfileTab === 'general' ? undefined : courseProfileTab,
            }),
          });
        }}
        page={search.courseProfilePage ?? 1}
        peopleView={search.courseProfilePeopleView ?? 'students'}
        completionFormCertificationTypeId={search.courseProfileCompletionFormCertificationType}
        q={search.courseProfileQ}
        registrationFilters={{
          certifiedFrom: search.courseProfileCertifiedFrom,
          certifiedTo: search.courseProfileCertifiedTo,
          costMax: search.courseProfileCostMax,
          costMin: search.courseProfileCostMin,
          instructorPaymentStatus: search.courseProfileInstructorPaymentStatus,
          registrationReferenceType: search.courseProfileRegistrationReferenceType,
          registrationType: search.courseProfileRegistrationType,
          studentPaymentStatus: search.courseProfileStudentPaymentStatus,
          submittedFrom: search.courseProfileSubmittedFrom,
          submittedTo: search.courseProfileSubmittedTo,
        }}
        renderTabLink={(item, options) => (
          <Link
            key={item.id}
            aria-current={options.isActive ? 'page' : undefined}
            className={options.className}
            params={{ courseId, locale }}
            search={(current) => ({
              ...resetCourseProfilePage(current),
              courseProfileCertificationState: undefined,
              courseProfileCompletionFormCertificationType: undefined,
              courseProfilePeopleView: undefined,
              courseProfileQ: undefined,
              courseProfileSort: undefined,
              courseProfileTab:
                item.id === 'general' ? undefined : (item.id as CrmCourseProfileTab),
            })}
            to="/$locale/crm/courses/$courseId"
          >
            {item.label}
          </Link>
        )}
        sort={search.courseProfileSort}
        tab={search.courseProfileTab ?? 'general'}
        viewerScope={viewerScope}
      />
    </main>
  );
}
